import React, { useState } from 'react';
import { useAuthStore } from '../../../stores/authStore';

interface ForgotPasswordFormProps {
  onBack: () => void;
}

export default function ForgotPasswordForm({ onBack }: ForgotPasswordFormProps) {
  const { forgotPassword } = useAuthStore();
  const [email, setEmail]     = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [sent, setSent]       = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) { setError('请输入邮箱地址'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setError('请输入有效的邮箱格式'); return; }
    setLoading(true);
    setError('');
    const { error: err } = await forgotPassword(email.trim());
    if (err) {
      // 后端失败文案本身就是中文（限流、服务异常等），原样透出比兜底一句「请稍后重试」有用
      setError(err.message || '发送失败，请稍后重试');
      setLoading(false);
    } else {
      setSent(true);
    }
  };

  if (sent) {
    return (
      <div className="flex flex-col items-center gap-6 py-4">
        <div className="w-16 h-16 bg-teal-50 rounded-full flex items-center justify-center">
          <i className="ri-mail-check-line text-teal-500 text-4xl"></i>
        </div>
        <div className="text-center">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">请查收邮件</h3>
          {/* 与后端 forgot-password 同一口径：账号不存在时后端也返回成功，所以这里
              必须说成条件句。写死「已发送至 xxx」会让填错邮箱的人坚信自己填对了。 */}
          <p className="text-sm text-gray-500 leading-relaxed">
            若 <span className="text-teal-600 font-medium">{email}</span> 已注册，
            重置链接已发送至该邮箱，请查收邮件并点击链接重置密码。
          </p>
          <p className="text-xs text-gray-400 mt-3">
            没收到？请检查邮箱是否填写正确，并可查看垃圾邮件。链接 1 小时内有效。
          </p>
        </div>
        <div className="w-full flex flex-col gap-2.5">
          <button
            onClick={onBack}
            className="w-full px-6 py-2.5 bg-teal-500 hover:bg-teal-600 text-white text-sm font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            返回登录
          </button>
          {/* 邮箱填错是「没收到」最常见的原因。邮箱保留在输入框里，改一个字母就能重发。 */}
          <button
            type="button"
            onClick={() => { setSent(false); setError(''); }}
            className="w-full px-6 py-2.5 border border-gray-200 hover:bg-gray-50 text-gray-600 text-sm font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            换个邮箱
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="text-sm text-gray-500 leading-relaxed p-3.5 bg-amber-50 border border-amber-100 rounded-lg">
        <div className="flex items-start gap-2">
          <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
            <i className="ri-information-line text-amber-500 text-sm"></i>
          </div>
          输入您注册时使用的邮箱地址，我们将向您发送一封包含重置链接的邮件。
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">邮箱地址</label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none w-10 h-full justify-center">
            <i className="ri-mail-line text-gray-400 text-lg"></i>
          </div>
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="请输入注册邮箱"
            autoFocus
            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500 transition-colors"
          />
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-100 rounded-lg">
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-error-warning-line text-red-500 text-sm"></i>
          </div>
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      <button
        type="submit"
        disabled={loading}
        className="w-full py-2.5 bg-teal-500 hover:bg-teal-600 disabled:bg-teal-300 text-white text-sm font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
      >
        {loading ? (
          <>
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            发送中...
          </>
        ) : '发送重置邮件'}
      </button>

      <button
        type="button"
        onClick={onBack}
        className="w-full py-2.5 border border-gray-200 hover:bg-gray-50 text-gray-600 text-sm font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap"
      >
        返回登录
      </button>
    </form>
  );
}
